import { EvidenceEnforcement } from './evidence.enforcement';
import { isEvidenceExplained } from './evidence.exception.type';
import { Task } from './task';
import { buildJudgedTask } from './task.completion.gate';
import { isEvidenceSectionCaptured, resolveEvidenceConfig, TASK_EVIDENCE_RESPONSE_FIELDS, TASK_REQUIREMENT_FLAG_FIELDS, TaskEvidenceKind } from './task.evidence';
import { isStampedTaskCompleted, isStampedTaskResolved } from './task.status.policy';

export enum TaskEvidenceOutcome {
    Captured = 'Captured',
    Exception = 'Exception',
    Outstanding = 'Outstanding',
    NoneRequired = 'NoneRequired',
    CompletedWithMissingEvidence = 'CompletedWithMissingEvidence',
    NotApplicable = 'NotApplicable',
}

/** Shared by every surface that shows an outcome, so the board and the detail pages name it alike. */
export const TASK_EVIDENCE_OUTCOME_LABELS: Record<TaskEvidenceOutcome, string> = {
    [TaskEvidenceOutcome.Captured]: 'Captured',
    [TaskEvidenceOutcome.Exception]: 'Exception',
    [TaskEvidenceOutcome.Outstanding]: 'Outstanding',
    [TaskEvidenceOutcome.NoneRequired]: 'None required',
    [TaskEvidenceOutcome.CompletedWithMissingEvidence]: 'Completed with missing evidence',
    [TaskEvidenceOutcome.NotApplicable]: 'Not applicable',
};

/** The two outcomes whose name alone does not say how the task got there (WBS-12340). */
export const TASK_EVIDENCE_OUTCOME_EXPLANATIONS: Partial<Record<TaskEvidenceOutcome, string>> = {
    [TaskEvidenceOutcome.CompletedWithMissingEvidence]: 'Completed by a user with the Complete tasks with missing evidence permission, or completed before evidence rules applied.',
    [TaskEvidenceOutcome.NotApplicable]: 'Closed without completing, for example Not Required.',
};

export interface TaskEvidenceOutcomeState {
    kind: TaskEvidenceKind;
    state: TaskEvidenceOutcome;
}

export const TASK_EVIDENCE_OUTCOME_FIELD = 'evidenceOutcome' satisfies keyof Task;

/** Every stored field the outcome is judged from, the outcome included; the form response itself lives elsewhere, so its own writers refresh after them. */
export const TASK_EVIDENCE_OUTCOME_INPUT_FIELDS = [...TASK_EVIDENCE_RESPONSE_FIELDS, ...TASK_REQUIREMENT_FLAG_FIELDS, 'status', 'taskStatus', 'active', 'evidenceException', 'formTemplate', 'links', TASK_EVIDENCE_OUTCOME_FIELD] as const satisfies readonly (keyof Task)[];

/** Per enabled evidence on a judged task; Optional evidence that was added shows Captured (D8). */
export function findTaskEvidenceStates(task: Partial<Task>): TaskEvidenceOutcomeState[] {

    return resolveEvidenceConfig(task).map(config => ({ kind: config.kind, state: judgeEvidenceState(task, config.kind, config.enforcement) }));
}

/** First match wins; judged on the task's own stored levels, so a SOR line's levels in force are already on it (D7). */
export function computeTaskEvidenceOutcome(task: Partial<Task>): TaskEvidenceOutcome {

    // Cancelling or declining a line deactivates its tasks without closing them, and no evidence can be captured on a task nobody sees (WBS-12437).
    if (task.active === false) return TaskEvidenceOutcome.NotApplicable;
    if (isStampedTaskResolved(task) && !isStampedTaskCompleted(task)) return TaskEvidenceOutcome.NotApplicable;

    const required = new Set(resolveEvidenceConfig(task).filter(config => config.enforcement !== EvidenceEnforcement.Optional).map(config => config.kind));
    const states = findTaskEvidenceStates(task).filter(evidence => required.has(evidence.kind)).map(evidence => evidence.state);

    if (!states.length) return TaskEvidenceOutcome.NoneRequired;
    if (states.every(state => state === TaskEvidenceOutcome.Captured)) return TaskEvidenceOutcome.Captured;
    if (!states.includes(TaskEvidenceOutcome.Outstanding)) return TaskEvidenceOutcome.Exception;

    return isStampedTaskCompleted(task) ? TaskEvidenceOutcome.CompletedWithMissingEvidence : TaskEvidenceOutcome.Outstanding;
}

/** A task about to be inserted has no stored form response and no exception of its own, so a copied `formCompleted` or exception must not count. */
export function withTaskEvidenceOutcome<T extends Partial<Task>>(task: T): T {

    return { ...task, evidenceOutcome: computeTaskEvidenceOutcome(buildJudgedTask(undefined, task)) };
}

function judgeEvidenceState(task: Partial<Task>, kind: TaskEvidenceKind, level: EvidenceEnforcement): TaskEvidenceOutcome {

    if (isEvidenceSectionCaptured(task, kind, true)) return TaskEvidenceOutcome.Captured;
    if (level === EvidenceEnforcement.Optional) return TaskEvidenceOutcome.NoneRequired;

    return isEvidenceExplained(task.evidenceException, { kind, level }) ? TaskEvidenceOutcome.Exception : TaskEvidenceOutcome.Outstanding;
}
